import { ApiPromise } from "@polkadot/api";
import { decodeAddress } from '@polkadot/util-crypto';
import { u8aToHex } from '@polkadot/util';
import { TPoolHistoryType } from "./types";
import { ethers } from "ethers";

export const ss58ToPublicKey = (ss58: string): string => {
  if (!ss58) throw new Error('Empty address');
  // If already hex, normalize and return
  if (ss58.startsWith('0x')) return ss58.toLowerCase();

  try {
    const pubKeyU8a = decodeAddress(ss58);
    return u8aToHex(pubKeyU8a);
  } catch (err: any) {
    throw new Error(`Invalid SS58 address: ${err?.message || err}`);
  }
};

export const formatAmount = (amount: string, isLong: boolean = false) => {
  if (!amount) return '0';
  // Remove trailing zeros after decimal point
  const formatted = Number(amount).toFixed(isLong ? 6 : 3).toString();
  if (Number(formatted) >= 1e9 || Number(formatted) <= -1e9) {
    return (Number(formatted) / 1e9).toFixed(isLong ? 6 : 2) + 'B';
  }
  if (Number(formatted) >= 1e6 || Number(formatted) <= -1e6) {
    return (Number(formatted) / 1e6).toFixed(isLong ? 6 : 2) + 'M';
  }
  if (Number(formatted) >= 1e3 || Number(formatted) <= -1e3) {
    return (Number(formatted) / 1e3).toFixed(isLong ? 6 : 2) + 'K';
  }
  return Number(formatted).toFixed(isLong ? 6 : 3);
};

export const dateDifference = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffSeconds = Math.floor(diffTime / 1000);
  if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''}`;
  }
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  }
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
};

export const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDateForChart = (timestamp: number, range: TPoolHistoryType) => {
  const date = new Date(timestamp);
  switch (range) {
    case 'day':
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'week':
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit' });
    case 'month':
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    case 'year':
      return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
    default:
      return date.toLocaleDateString();
  }
};

export const formatAddress = (address: string | undefined) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const getAccountBalance = async (api: ApiPromise, address: string) => {
  const accountInfo = await api.query.system.account(address);
  return accountInfo.toHuman() as { data: { free: string } };
};

const formatCompactNumber = (num: number): string => {
  const absNum = Math.abs(num);
  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  }
  if (absNum >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  }
  if (absNum >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  }
  return num.toString();
};


export const formatNumber = (num: number): string => {
  if (num >= 1e9) {
    return (num / 1e9).toFixed(2) + 'B';
  }
  if (num >= 1e6) {
    return (num / 1e6).toFixed(2) + 'M';
  }
  if (num >= 1e3) {
    return (num / 1e3).toFixed(2) + 'K';
  }
  return num.toFixed(2);
};

export const parseContractEvents = (receipt: any, poolContract: ethers.Contract) => {
  const events = receipt.logs
    .map((log: any) => {
      try {
        return poolContract.interface.parseLog(log);
      } catch {
        return null;
      }
    })

  console.log("events length", events.length);

  for (const event of events) {
    console.log("event catched", event?.args);
    console.log('event name', event?.name);
  }
}